import axios, { AxiosError, AxiosRequestConfig } from "axios";

import { IApiService, ApiRequestOptions } from "./IApiService";
import { TokenService } from "./TokenService";
import { ITokenService } from "./ITokenService";
import { Token } from "./Token";
import { getApiUrl, getTokenHeaders, getNoCacheHeaders } from "../commUtil";

interface ApiError {
    title: string;
    errorCode: number;
    detail: string;
}

function isString(object: any): boolean {
    return typeof object === "string";
}

function isApiError(object: any): object is ApiError {
    return object && !isString(object) && "detail" in object;
}

export class ApiService implements IApiService {
    private readonly baseUrl: string = "";
    private readonly tokenService: ITokenService;

    constructor(baseUrl: string, language: string = "") {
        this.baseUrl = baseUrl;

        if (this.baseUrl.endsWith("/")) {
            this.baseUrl = this.baseUrl.substr(0, this.baseUrl.length - 1);
        }

        this.tokenService = TokenService.getInstance(baseUrl, language);
    }

    public get<T>(url: string, params?: object, preventCache?: boolean): Promise<T> {
        const doRequest = (token: Token): Promise<T> => {
            const fullUrl = getApiUrl(this.baseUrl, url);
            const headers = Object.assign(
                {},
                getTokenHeaders(token),
                preventCache ? getNoCacheHeaders() : {}
            );
            return axios.get(fullUrl, { headers, params }).then(response => response.data as T);
        };

        return this.doRequestWithToken(doRequest);
    }

    public post<T>(
        url: string,
        data: any,
        headers?: object,
        options?: ApiRequestOptions
    ): Promise<T> {
        const doRequest = (token: Token): Promise<T> => {
            const fullUrl = getApiUrl(this.baseUrl, url);
            headers = Object.assign({}, getTokenHeaders(token), headers);
            return axios
                .post(fullUrl, data, getAxiosRequestConfig(headers, options))
                .then(response => response.data as T);
        };

        return this.doRequestWithToken(doRequest);
    }

    public put<T>(
        url: string,
        data: any,
        headers?: object,
        options?: ApiRequestOptions
    ): Promise<T> {
        const doRequest = (token: Token): Promise<T> => {
            const fullUrl = getApiUrl(this.baseUrl, url);
            headers = Object.assign({}, getTokenHeaders(token), headers);
            return axios
                .put(fullUrl, data, getAxiosRequestConfig(headers, options))
                .then(response => response.data as T);
        };

        return this.doRequestWithToken(doRequest);
    }

    public delete<T>(url: string, headers?: object, options?: ApiRequestOptions): Promise<T> {
        const doRequest = (token: Token): Promise<T> => {
            const fullUrl = getApiUrl(this.baseUrl, url);
            headers = Object.assign({}, getTokenHeaders(token), headers);
            return axios
                .delete(fullUrl, getAxiosRequestConfig(headers, options))
                .then(response => response.data as T);
        };

        return this.doRequestWithToken(doRequest);
    }

    public signOut(): Promise<void> {
        return this.tokenService.revokeToken();
    }

    private doRequestWithToken<T>(requestFn: (token: Token) => Promise<T>): Promise<T> {
        const getTokenAndExecuteRequest = (forceRefresh: boolean): Promise<T> => {
            return this.tokenService.getToken(forceRefresh).then(requestFn);
        };

        const handleError = (error: AxiosError): Promise<T> => {
            if (error.response) {
                if (error.response.status === 401) {
                    // This error typically means that our current token, while unexpired, has
                    // become invalid, maybe because the server was restarted. Try refreshing
                    // the token. If that doesn't work, a new token will be requested.
                    return getTokenAndExecuteRequest(true);
                }

                // If we don't recognize the error, pass it on for higher level handling.
                if (isApiError(error.response.data)) {
                    return Promise.reject((error.response.data as ApiError).detail);
                }
            }

            // If we can't get any specific detail from the error, reject with the error message,
            // or failing that, the error itself.
            return Promise.reject(error.message || error);
        };

        return getTokenAndExecuteRequest(false).catch(handleError);
    }
}

function getAxiosRequestConfig(headers?: object, options?: ApiRequestOptions): AxiosRequestConfig {
    return {
        headers,
        onUploadProgress: (options && options.onUploadProgress) || axios.defaults.onUploadProgress,
        validateStatus: (options && options.validateStatus) || axios.defaults.validateStatus,
        cancelToken: (options && options.cancelToken) || axios.defaults.cancelToken,
    };
}
